import {
    ECSConfigKey,
    ECSConfigService,
    FlightSettingsService,
    ShiftRequestDataService,
    TimeClockDataService,
    UserStorageService
    } from "sh-services";
import {
    FlightKeys,
    UserStorageKeys
    } from "sh-models";
import { getAvailabilities } from "sh-availability-store";
import { getUniqueShifts } from "sh-uniqueshift-store";
import { getUniqueSubshifts } from "sh-uniquesubshift-store";
import initializeSchedulesViewState from "../actions/initializeSchedulesViewState";
import { setShowConflictsFromUserSetting } from "../index";
import { initializeScheduleViewAndFilters } from "../common/initializeScheduleViewAndFilters";
import { orchestrator } from "satcheljs";
import { SchedulesViewStateStore } from "sh-application/components/schedules/lib/store/schema/SchedulesViewStateStore";
import { TeamStore, TeamStoreSchema } from "sh-team-store";
import { TimeClockEntryStore } from "sh-stores/sh-timeclock-store";
import { trace } from "owa-trace";
import { UserStore } from "sh-stores";

/**
 * This orchestrator initializes the schedules view state.  It should be used when bootstrapping a team schedule,
 * either during app launch or when switching teams.
 * - Calculate current schedule view range
 * - Sets up schedule view state settings
 * - Triggers fetch of schedule data for the current schedule view as needed
 */

export const initializeSchedulesViewStateOrchestrator = orchestrator(initializeSchedulesViewState, async actionMessage => {
        if (!actionMessage.viewState) {
            return;
        }

        try {
            // We populate the view state and trigger requests for additional data
            const populatePromise = populateViewState(actionMessage.viewState);

            saveAsMostRecentTeam();

            await populatePromise;
        } catch (e) {
            trace.warn(`initializeSchedulesViewState is failing with error: ${e}`);
        }
    }
);

async function populateViewState(viewState: SchedulesViewStateStore): Promise<void> {
    // Setup the schedule view and load schedule data via the single authoritative initializer.
    // This resolves the correct persisted (or default) view BEFORE any schedule data fetch is dispatched,
    // so exactly one correct fetch fires. Fired in parallel with the auxiliary loads below.
    const initPromise = initializeScheduleViewAndFilters(viewState, TeamStore().teamId);

    // Trigger load of auxiliary schedule related data
    // These are triggered in parallel and their data will light up in the schedule view as they are loaded.
    updateUniqueShiftsData();
    updateUniqueSubshiftsData();
    updateAvailabilitiesData();
    updateShiftRequestsData();
    updateTimeClockEntryData();
    updateConflictHiddenUserSettingData();

    await initPromise;
}

/**
 * Ensure that unique shifts are loaded
 */
function updateUniqueShiftsData() {
    const teamStore = TeamStore();
    if (teamStore) {
        getUniqueShifts(teamStore.tenantId, teamStore.teamId);
    }
}

/**
 * Ensure that unique shifts are loaded
 */
function updateConflictHiddenUserSettingData() {
    const userStore = UserStore();
    if (userStore && userStore.userSettings) {
        setShowConflictsFromUserSetting(!userStore.userSettings.hideConflictManagement);
    }
}

/**
 * Ensure that unique subshifts are loaded
 */
function updateUniqueSubshiftsData() {
    const teamStore = TeamStore();
    if (teamStore) {
        getUniqueSubshifts(teamStore.tenantId, teamStore.teamId);
    }
}

/**
 * Ensure that availability data is loaded
 */
function updateAvailabilitiesData() {
    if (isAvailabilitiesEnabled()) {
        const teamStore = TeamStore();
        if (teamStore) {
            getAvailabilities(teamStore.tenantId, teamStore.teamId);
        }
    }
}

/**
 * Load shift requests data
 */
function updateShiftRequestsData() {
    if (isOpenShiftRequestsEnabled()) {
        const teamStore = TeamStore();
        if (teamStore) {
            // all of the teams active shift requests will be returned in the first request.
            // If the number of active requests is greater than our page size (25), service will still
            // return all of the active records and a single inactive record. If the number of active requests is smaller than
            // our page size, service will return as many inactive records as it can to fill up the page. In the Requests UI, we
            // then show a "Load more" link for the user to continue loading the remaining inactive requests.
            ShiftRequestDataService.getShiftRequestsForTeam(teamStore.tenantId, teamStore.teamId);
        }
    }
}

/*
* Load time clock entry data
*/
function updateTimeClockEntryData() {
    const timeclockEnabledForTeam = TeamStore().team && TeamStore().team.timeClockEnabled;
    const teamStore = TeamStore();
    if (isTimeClockOnWebEnabled() && timeclockEnabledForTeam && teamStore) {
        const teamId = TeamStore().teamId;
        const timeClockEntryStore = TimeClockEntryStore();
        if (timeClockEntryStore && teamId) {
            TimeClockDataService.getLatestTimeClockEntryForMember(teamId);
        }
    }
}

/**
 * Instead of setting the mostRecentTeam on navigate, we do it here, on load of Schedules,
 * which won't ever happen if the team doesn't exist.
 */
function saveAsMostRecentTeam() {
    const teamStore: TeamStoreSchema = TeamStore();
    if (teamStore && teamStore.teamId) {
        UserStorageService.setItem(UserStorageKeys.MostRecentTeamId, teamStore.teamId);
    }
}

/**
 * Function to determine if availabilities are enabled
 */
function isAvailabilitiesEnabled(): boolean {
    return ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableScheduleAvailability)
        && FlightSettingsService.isFlightEnabled(FlightKeys.EnableScheduleAvailability);
}

/**
 * Returns true if the open shift requests feature is enabled
 */
function isOpenShiftRequestsEnabled(): boolean {
    return ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableOpenShiftRequests) && FlightSettingsService.isFlightEnabled(FlightKeys.EnableOpenShiftRequestsOnWeb); // TODO: flight check;
}

/**
 * Returns true if the time clock on web feature is enabled
 */
function isTimeClockOnWebEnabled(): boolean {
    return ECSConfigService.isECSFeatureEnabled(ECSConfigKey.EnableTimeClockOnWeb);
}
